import analysisContextBubbleIcon from '@disy/cadenza-icons/analysis-context-bubble.svg';
import indicatorAnalysisContextDataViewIcon from '@disy/cadenza-icons/analysis-context-data-view.svg';
import bubbleIcon from '@disy/cadenza-icons/bubble.svg';
import { createButton, createIconButton } from '@disy/ui/button';
import { icon, iconStack } from '@disy/ui/icon';
import { KEEP_POPUP_OPEN_CLASS, setupPopupButton } from '@disy/ui/popup';

import 'ui/compact-header/compact-header.css';

import { getStore } from 'cadenza/store/cadenza-store';
import { isEmptyBundleAnalyticLine } from 'cadenza/workbook/api/data-view-visualization-key';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import {
  getDataViewAnalysisContextDataViews,
  getReferenceGroupsWithDataViews,
  isReferenceLayer,
  isReferenceLayerById,
} from 'cadenza/workbook/map/workbook-layer-utils';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id.gen';
import type { View } from 'cadenza/workbook/view/view';
import type { ViewFiltersFlyoutOptions } from 'cadenza/workbook/view/view-filters-button/view-filters-flyout';

import './view-filters-button.css';
import i18n from './view-filters-button.properties';

export interface UpdateableElement extends HTMLElement {
  update?: (view: View) => void;
  disconnectPopupEvent?: () => void;
}

export function createViewFiltersButton(view: View, asIconButton: boolean): UpdateableElement {
  const button: UpdateableElement =
    asIconButton ?
      createIconButton(icon(indicatorAnalysisContextDataViewIcon), i18n('view.filters.button.label'), {
        variant: 'borderless',
        size: 's',
        styleClass: 'view-filters-button',
      })
    : createButton([icon(indicatorAnalysisContextDataViewIcon), i18n('view.filters.button.label')], {
        attrs: { role: 'menuitem' },
      });
  button.update = (newView) => {
    const dataViews = getDataViewAnalysisContextDataViews(newView);

    const referenceGroups = getReferenceGroupsWithDataViews(getStore().getState(), view.viewId);
    const isVisible = Boolean(dataViews.length || referenceGroups.length);
    button.hidden = !isVisible;
    if (isVisible) {
      const dataViewWithLocalConditions = dataViews
        .flatMap((dataView) => dataView.conditions)
        // Filter out spatial conditions, as they are added to the local conditions only in order to provide the affected attribute for each data view
        .filter((condition) => condition.type !== 'PolygonCondition');
      const hasSetLocalFilters = dataViewWithLocalConditions.some(
        (condition) => (condition.values?.valueItems.length ?? 0) > 0,
      );
      // replacing the button is not trivial since we would also have to update the references
      // in the usages of this method. Therefore we swap the icon manually
      // order of array matters since d-icon-stack will contain d-icon elements
      for (const cssClass of ['.d-icon-stack', '.d-icon']) {
        const queryResult = button.querySelector(cssClass);
        if (queryResult) {
          if (hasSetLocalFilters) {
            queryResult.replaceWith(
              iconStack([
                { icon: indicatorAnalysisContextDataViewIcon },
                { icon: analysisContextBubbleIcon, styles: ['primary'] },
              ]),
            );
          } else {
            queryResult.replaceWith(icon(indicatorAnalysisContextDataViewIcon));
          }

          break;
        }
      }
      connectPopup(button, newView);
    }
  };
  connectPopup(button, view);
  return button;
}

function connectPopup(button: UpdateableElement, view: View) {
  if (button.disconnectPopupEvent) {
    button.disconnectPopupEvent();
  }
  button.disconnectPopupEvent = setupPopupButton(button, () => getPopupContent(view), {
    styleClass: ['d-dataview-filters--flyout', KEEP_POPUP_OPEN_CLASS],
  });
}

async function getPopupContent(view: View) {
  const referenceGroups = getReferenceGroupsWithDataViews(getStore().getState(), view.viewId);
  if (
    view.dataViews.filter((dataView) => !isEmptyBundleAnalyticLine(dataView.visualizationKey)).length > 1 ||
    referenceGroups.length > 1 ||
    (view.dataViews.length === 1 && isReferenceLayerById(view.viewId, view.dataViews[0].dataViewId)) ||
    (referenceGroups.length === 1 && isReferenceLayer(referenceGroups[0]))
  ) {
    return showMultipleDataViewsFilters(view.viewId);
  }
  return showViewFiltersFlyout(view.viewId, view.dataViews[0].dataViewId);
}

async function showMultipleDataViewsFilters(viewId: ViewId) {
  const { MultipleViewsFilters } = await import('cadenza/workbook/view/view-filters-button/multiple-dataviews-filters');
  return new MultipleViewsFilters(viewId);
}

async function showViewFiltersFlyout(
  viewId: ViewId,
  dataViewId: DataViewId,
  viewFiltersFlyoutOptions?: ViewFiltersFlyoutOptions,
) {
  const { ViewFiltersFlyout } = await import('cadenza/workbook/view/view-filters-button/view-filters-flyout');
  return new ViewFiltersFlyout(viewId, dataViewId, viewFiltersFlyoutOptions);
}

export function createViewFilterIndicatorButtonIcon() {
  const filterIndicatorClass = 'd-multiple-dataviews-filters--filter-indicator';
  const indicatorIcon = iconStack([{ icon: bubbleIcon, styles: ['primary'] }], {
    size: 'xs',
  }) as UpdateableElement;
  indicatorIcon.classList.add(filterIndicatorClass);
  indicatorIcon.update = (newView) => {
    const dataViews = getDataViewAnalysisContextDataViews(newView);
    const dataViewWithLocalConditions = dataViews
      .flatMap((dataView) => dataView.conditions)
      // Filter out spatial conditions, as they are added to the local conditions only in order to provide the affected attribute for each data view
      .filter((condition) => condition.type !== 'PolygonCondition');
    const hasSetLocalFilters = dataViewWithLocalConditions.some(
      (condition) => (condition.values?.valueItems.length ?? 0) > 0,
    );
    const indicatorElement = newView.querySelector<HTMLElement>(`.${filterIndicatorClass}`);
    if (indicatorElement) {
      indicatorElement.hidden = !hasSetLocalFilters;
    }
  };
  return indicatorIcon;
}
